/** Bind values accepted by expo-sqlite async APIs and our web shim. */
export type DrizzleAsyncSQLiteBindValue = null | string | number | boolean | Uint8Array;

export interface DrizzleAsyncSQLiteRawResult<T> {
  getAllAsync: () => Promise<T[]>;
}

export interface DrizzleAsyncSQLiteStatement {
  executeForRawResultAsync: <T>(
    params?: readonly DrizzleAsyncSQLiteBindValue[],
  ) => Promise<DrizzleAsyncSQLiteRawResult<T>>;
  finalizeAsync: () => Promise<void>;
}

/** Minimal async SQLite client surface used by `ExpoSQLiteAsyncSession`. */
export interface DrizzleAsyncSQLiteClient {
  execAsync?: (source: string) => Promise<void>;
  getAllAsync: <T extends Record<string, unknown> = Record<string, unknown>>(
    sql: string,
    params?: readonly DrizzleAsyncSQLiteBindValue[],
  ) => Promise<T[]>;
  getFirstAsync: <T extends Record<string, unknown> = Record<string, unknown>>(
    sql: string,
    params?: readonly DrizzleAsyncSQLiteBindValue[],
  ) => Promise<T | null>;
  prepareAsync: (sql: string) => Promise<DrizzleAsyncSQLiteStatement>;
  runAsync: (
    sql: string,
    params?: readonly DrizzleAsyncSQLiteBindValue[],
  ) => Promise<{ changes: number; lastInsertRowId: number }>;
  withTransactionScope?: <T>(body: (client: DrizzleAsyncSQLiteClient) => Promise<T>) => Promise<T>;
}

/** Accepted drizzle clients: native expo-sqlite and web SQLocal shim. */
export type AnyDrizzleAsyncSQLiteClient = DrizzleAsyncSQLiteClient;

export function isDrizzleAsyncSQLiteClient(value: unknown): value is DrizzleAsyncSQLiteClient {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return (
    typeof Reflect.get(value, "runAsync") === "function" &&
    typeof Reflect.get(value, "getAllAsync") === "function" &&
    typeof Reflect.get(value, "getFirstAsync") === "function" &&
    typeof Reflect.get(value, "prepareAsync") === "function"
  );
}

export function toDrizzleAsyncSQLiteClient(value: unknown): DrizzleAsyncSQLiteClient {
  if (!isDrizzleAsyncSQLiteClient(value)) {
    throw new TypeError(
      "Expected an async SQLite client compatible with drizzle expo-sqlite-async",
    );
  }
  return value;
}
